import { Component } from '@theme/component';

const ACTIVE_THRESHOLD = 0.15;

// Each phase is a fraction of viewport height, tuned independently of any slide's
// own sticky/media height:
// ENTER  - the slide scrolling up from the bottom of the viewport to the pin point.
// EXPAND - once pinned, how long it takes the card to grow to full size.
// HOLD   - a short deliberate pause at full size, so there's time to read it.
// EXIT   - after the hold, how long it takes the card to accelerate off-screen.
//          This is intentionally shorter than the card's own height so it doesn't
//          just sit there for a full "sticky-height" of scroll before releasing.
const ENTER_VH = 1.0;
const EXPAND_VH = 0.5;
const HOLD_VH = 0.2;
const EXIT_VH = 0.35;

// One consistent frame shape for every mobile slide, regardless of each photo's own
// proportions, so all slides are the same size. object-fit: cover fills this frame from
// each image, cropping a little where a photo doesn't natively match (same tradeoff
// desktop already makes with its own fixed section-height setting).
const MOBILE_ASPECT_RATIO = 1.5;

/** @param {number} value @param {number} [min] @param {number} [max] */
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

/**
 * @typedef {Object} LayeredSlideshowRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [slides]
 */

/**
 * Drives a scroll-scrubbed "expand" effect: each slide is a tall track with a
 * sticky inner viewport. On every scroll (rAF-throttled):
 *  - a `--progress` custom property (0 -> 1) scales the panel from a collapsed
 *    inset card up to full-bleed as the slide enters and reaches its pin point,
 *    and its text content fades in once past ACTIVE_THRESHOLD.
 *  - once the pin+hold window has elapsed, an additional `--exit-offset`
 *    transform accelerates the card off-screen within EXIT_VH of scrolling,
 *    rather than the sticky-height's worth of scroll it would otherwise take
 *    to scroll away naturally.
 *
 * @extends {Component<LayeredSlideshowRefs>}
 */
export class LayeredSlideshowComponent extends Component {
  requiredRefs = ['container'];

  #ticking = false;
  #reducedMotion = false;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { slides } = this.refs;
    if (!slides?.length) return;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Reduced motion: the stylesheet shows every slide at full size statically, nothing to scrub.
    if (this.#reducedMotion) return;

    this.#layout();

    this.#abort = new AbortController();
    const opts = { passive: true, signal: this.#abort.signal };
    window.addEventListener('scroll', () => this.#requestTick(), opts);
    window.addEventListener(
      'resize',
      () => {
        this.#layout();
        this.#requestTick();
      },
      opts
    );

    // Text metrics can shift slightly once web fonts finish loading; re-measure once they're ready.
    document.fonts?.ready.then(() => {
      this.#layout();
      this.#requestTick();
    });

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  #requestTick() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => this.#update());
  }

  /**
   * Sizes each slide's scroll track to fit its measured sticky height plus the
   * pin-engagement window (expand + hold). This only needs to run on connect
   * and resize, not every scroll frame, since it doesn't otherwise change.
   */
  #layout() {
    const { slides } = this.refs;
    if (!slides?.length) return;

    const vh = window.innerHeight;
    const pinEngageDist = (EXPAND_VH + HOLD_VH) * vh;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    /** @type {{ sticky: HTMLElement, contentHeight: number }[]} */
    const measurements = [];

    for (const slide of slides) {
      const sticky = /** @type {HTMLElement | null} */ (slide.querySelector('.layered-slideshow__sticky'));
      if (!sticky) continue;

      // Make sure the pinned viewport is never shorter than its own text content needs
      // (mainly matters on narrow widths where text wraps taller). scrollHeight always
      // reflects the true content height even where it's currently being clipped, so
      // this is a no-op wherever the content already fits.
      const content = slide.querySelector('.layered-slideshow__content');
      const inner = /** @type {HTMLElement | null} */ (content?.querySelector('.group-block-content'));
      let contentHeight = 0;
      if (content && inner) {
        const cs = getComputedStyle(content);
        const verticalPadding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        contentHeight = inner.scrollHeight + verticalPadding;
      }

      measurements.push({ sticky, contentHeight });
    }

    if (isMobile) {
      // Every mobile slide shares one identical frame height, taken from whichever
      // slide needs the most room (its own image's aspect ratio vs. its text content),
      // rather than each slide sizing itself independently. object-fit: contain (set in
      // the stylesheet) then shows each image's full, uncropped content within that
      // shared frame, rather than object-fit: cover cropping to fill it.
      const stickyWidth = measurements[0]?.sticky.getBoundingClientRect().width || 0;
      const aspectHeight = stickyWidth / MOBILE_ASPECT_RATIO;
      const targetHeight = Math.max(aspectHeight, ...measurements.map((m) => m.contentHeight));
      for (const { sticky } of measurements) {
        sticky.style.minHeight = '';
        sticky.style.height = `${targetHeight}px`;
      }
    } else {
      for (const { sticky, contentHeight } of measurements) {
        sticky.style.height = '';
        sticky.style.minHeight = `${contentHeight}px`;
      }
    }

    for (const slide of slides) {
      const sticky = /** @type {HTMLElement | null} */ (slide.querySelector('.layered-slideshow__sticky'));
      if (!sticky) continue;
      slide.style.height = `${sticky.offsetHeight + pinEngageDist}px`;
    }
  }

  #update() {
    this.#ticking = false;
    const { slides } = this.refs;
    if (!slides?.length) return;

    const vh = window.innerHeight;
    const expandDist = EXPAND_VH * vh;
    const holdDist = HOLD_VH * vh;
    const exitDist = EXIT_VH * vh;
    const pinEngageDist = expandDist + holdDist;

    for (const slide of slides) {
      const sticky = /** @type {HTMLElement | null} */ (slide.querySelector('.layered-slideshow__sticky'));
      if (!sticky) continue;

      const trackRect = slide.getBoundingClientRect();
      const stickyHeight = sticky.offsetHeight;

      // Grow phase: card scales from collapsed to full as the slide enters and reaches the pin point.
      const growStart = vh;
      const growEnd = -expandDist;
      const growTotal = growStart - growEnd;
      const growProgress =
        growTotal > 0 ? clamp((growStart - trackRect.top) / growTotal) : trackRect.top <= growEnd ? 1 : 0;
      const isActive = growProgress > ACTIVE_THRESHOLD;

      slide.style.setProperty('--progress', String(growProgress));
      slide.toggleAttribute('data-active', isActive);

      // Exit phase: once past the pin-engagement window (expand + hold), accelerate the
      // card off-screen within exitDist instead of its own full sticky height of scroll.
      const pastPinPoint = Math.max(0, -trackRect.top - pinEngageDist);
      const exitT = exitDist > 0 ? clamp(pastPinPoint / exitDist) : pastPinPoint > 0 ? 1 : 0;
      const exitOffsetPx = pastPinPoint - exitT * stickyHeight;
      sticky.style.setProperty('--exit-offset', `${exitOffsetPx}px`);

      const video = slide.querySelector('video');
      if (video) {
        if (isActive && video.paused) video.play().catch(() => {});
        if (!isActive && !video.paused) video.pause();
      }
    }
  }
}

customElements.define('layered-slideshow-component', LayeredSlideshowComponent);
